import { apiRequest, apiUpload } from '../api';

export type RefType = 'formats' | 'hosts' | 'maps' | 'mods';

/** Тип карты: обычная / командная / КБ. */
export type MapType = 'regular' | 'team' | 'cb';
export const MAP_TYPES: readonly MapType[] = ['regular', 'team', 'cb'];

/** Формат командной карты (учитывается только у карт с типом team). */
export type TeamFormat = '2v2' | '3v3' | '4v4' | '2v2v2' | '2v2v2v2';
export const TEAM_FORMATS: readonly TeamFormat[] = ['2v2', '3v3', '4v4', '2v2v2', '2v2v2v2'];

export interface GameFormat {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
  elo_weight: number;
  is_team: boolean;
  team_size: number;
  created_at: string;
}

export interface GameHost {
  id: number;
  name: string;
  aka: string | null;
  player_id: number | null;
  created_at: string;
}

export interface GameMap {
  id: number;
  name: string;
  /** Альтернативное название (локализация клиента). */
  alt_name: string | null;
  /** Типы карты — независимые флаги (обычная / командная / КБ). Может быть пусто. */
  map_types: MapType[];
  /** Форматы командной карты (2на2, 3на3, 4на4, 2на2на2, 2на2на2на2) — только для типа team. */
  team_formats: TeamFormat[];
  /** Рекомендуемое число игроков (может быть не задано). */
  player_count: number | null;
  /** Автор карты (опционально). */
  author: string | null;
  /** Ссылка на карту/страницу автора (опционально). */
  link: string | null;
  /** Пометка «дисбалансная карта» (ставит модератор). */
  unbalanced: boolean;
  /** Путь картинки в бакете Supabase Storage (или null). */
  image_path: string | null;
  /** Готовый публичный URL картинки (добавляет сервер). */
  image_url: string | null;
  /** Мягкое удаление: карта скрыта из выбора, но игры на неё ссылаются. */
  deleted_at: string | null;
  created_at: string;
}

export interface GameMod {
  id: number;
  name: string;
  /** Автор мода (опционально). */
  author: string | null;
  /** Краткое описание мода (опционально). */
  description: string | null;
  /** Battle.net id настроек лобби мода в реплеях (сайт запоминает при сохранении игры из реплея). */
  replay_namespaces?: number[] | null;
  created_at: string;
}

export type RefItem = GameFormat | GameHost | GameMap | GameMod;

export async function listRefs<T extends RefItem>(refType: RefType): Promise<T[]> {
  const res = await apiRequest<{ items: T[] }>(`/api/game-refs/${refType}`);
  return res.items;
}

export async function createRef<T extends RefItem>(
  refType: RefType,
  body: Record<string, unknown>,
  token: string | null
): Promise<T> {
  const res = await apiRequest<{ item: T }>(`/api/game-refs/${refType}`, {
    method: 'POST',
    token,
    body,
  });
  return res.item;
}

export async function updateRef<T extends RefItem>(
  refType: RefType,
  id: number,
  body: Record<string, unknown>,
  token: string | null
): Promise<T> {
  const res = await apiRequest<{ item: T }>(`/api/game-refs/${refType}/${id}`, {
    method: 'PATCH',
    token,
    body,
  });
  return res.item;
}

export async function deleteRef(
  refType: RefType,
  id: number,
  token: string | null
): Promise<void> {
  await apiRequest(`/api/game-refs/${refType}/${id}`, {
    method: 'DELETE',
    token,
  });
}

/** Загружает картинку карты (модератор/ghost/admin). */
export async function uploadMapImage(
  id: number,
  file: File,
  token: string | null
): Promise<GameMap> {
  const res = await apiUpload<{ item: GameMap }>(
    `/api/game-refs/maps/${id}/image`,
    file,
    file.name,
    token,
    undefined,
    file.type || 'application/octet-stream'
  );
  return res.item;
}

/** Удаляет картинку карты. */
export async function deleteMapImage(
  id: number,
  token: string | null
): Promise<GameMap> {
  const res = await apiRequest<{ item: GameMap }>(`/api/game-refs/maps/${id}/image`, {
    method: 'DELETE',
    token,
  });
  return res.item;
}